// Platform API client — Phase 2C.
// Talks same-origin to our FastAPI proxy at /api/platform/* and /api/auth/login.
// Bearer JWT in Authorization header. Token in sessionStorage only.

const TOKEN_KEY = "shelflife_token";
const USER_KEY = "shelflife_user";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export const tokenStore = {
  get() {
    try {
      return sessionStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token) {
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* ignore quota */
    }
  },
  clear() {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
    } catch {
      /* ignore */
    }
  },
  getUser() {
    try {
      const raw = sessionStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  },
  setUser(user) {
    try {
      sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {
      /* ignore */
    }
  },
};

let onUnauthorizedHandler = null;
export const setOnUnauthorized = (fn) => {
  onUnauthorizedHandler = fn;
};

const url = (path) => `${API_BASE}${path}`;

export const login = async ({ email, password }) => {
  const resp = await fetch(url("/api/auth/login"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ email, password }),
  });
  let payload = null;
  try {
    payload = await resp.json();
  } catch {
    payload = null;
  }
  if (!resp.ok) {
    const message =
      (payload && (payload.detail || payload.message)) ||
      (resp.status === 401 ? "Invalid email or password" : `Login failed (${resp.status})`);
    const err = new Error(typeof message === "string" ? message : JSON.stringify(message));
    err.status = resp.status;
    err.payload = payload;
    throw err;
  }
  if (!payload || !payload.token || !payload.user) {
    throw new Error("Unexpected login response");
  }
  return { token: payload.token, user: payload.user };
};

export const platformGet = async (path) => {
  const token = tokenStore.get();
  const headers = { Accept: "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;

  let resp;
  try {
    resp = await fetch(url(path), { method: "GET", headers });
  } catch (e) {
    const err = new Error("Cannot reach ShelfLife preview");
    err.kind = "network";
    throw err;
  }

  if (resp.status === 401) {
    tokenStore.clear();
    if (onUnauthorizedHandler) onUnauthorizedHandler();
    const err = new Error("Session expired");
    err.status = 401;
    err.kind = "unauthorized";
    throw err;
  }
  if (resp.status === 403) {
    const err = new Error("Not authorized");
    err.status = 403;
    err.kind = "forbidden";
    throw err;
  }
  if (resp.status === 404) {
    const err = new Error("Not found");
    err.status = 404;
    err.kind = "not_found";
    throw err;
  }
  if (!resp.ok) {
    const err = new Error(`Request failed (${resp.status})`);
    err.status = resp.status;
    err.kind = "error";
    throw err;
  }
  try {
    return await resp.json();
  } catch {
    return null;
  }
};

// Phase 2C live-flag helpers
export const isLiveMode = () => process.env.REACT_APP_OPS_CONSOLE_LIVE === "true";
export const isSurfaceLive = (flagName) =>
  isLiveMode() && process.env[`REACT_APP_LIVE_${flagName}`] === "true";

// Phase 3B.2 — narrow write helper for the tenant-drafts transaction.
// Used only by the wizard; gated by REACT_APP_LIVE_TENANT_PROVISIONING.
export const platformPost = async (path, body) => {
  const token = tokenStore.get();
  const user = tokenStore.getUser();
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  if (user && user.role) headers["X-Operator-Role"] = user.role;

  let resp;
  try {
    resp = await fetch(url(path), {
      method: "POST",
      headers,
      body: JSON.stringify(body || {}),
    });
  } catch (e) {
    const err = new Error("Cannot reach ShelfLife preview");
    err.kind = "network";
    throw err;
  }
  let payload = null;
  try {
    payload = await resp.json();
  } catch {
    payload = null;
  }
  if (resp.status === 401) {
    tokenStore.clear();
    if (onUnauthorizedHandler) onUnauthorizedHandler();
    const err = new Error("Session expired");
    err.status = 401;
    err.kind = "unauthorized";
    throw err;
  }
  if (!resp.ok) {
    const detail =
      (payload && (payload.detail || payload.message)) ||
      `Request failed (${resp.status})`;
    const err = new Error(typeof detail === "string" ? detail : JSON.stringify(detail));
    err.status = resp.status;
    err.payload = payload;
    err.kind =
      resp.status === 409 ? "conflict" :
      resp.status === 400 ? "validation" :
      resp.status === 403 ? "forbidden" :
      resp.status === 502 ? "upstream_down" :
      "error";
    throw err;
  }
  return payload;
};
